import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalLink, Pencil, Trash2, X } from "lucide-react";
import { format } from "date-fns";
import type { Customer } from "@/hooks/useCustomers";

const TYPE_LABELS: Record<string, string> = {
  general_contractor: "General Contractor",
  property_manager: "Property Manager",
  owner: "Owner",
  insurance_company: "Insurance Co.",
  other: "Other",
};

const SYNC_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  synced: { label: "Synced", variant: "default" },
  pending: { label: "Pending", variant: "secondary" },
  error: { label: "Error", variant: "destructive" },
  not_synced: { label: "Not Synced", variant: "outline" },
};

interface CustomerDetailPanelProps {
  customer: Customer;
  projectCounts?: { total: number; active: number };
  canManage: boolean;
  isDeleting: boolean;
  onClose: () => void;
  onViewFull: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const CustomerDetailPanel = ({
  customer,
  projectCounts,
  canManage,
  isDeleting,
  onClose,
  onViewFull,
  onEdit,
  onDelete,
}: CustomerDetailPanelProps) => {
  const syncBadge = (status: string | null) => {
    const s = SYNC_BADGE[status || "not_synced"] || SYNC_BADGE.not_synced;
    return <Badge variant={s.variant} className="text-[10px]">{s.label}</Badge>;
  };

  return (
    <div className="flex flex-col h-full border-l bg-background">
      {/* Header */}
      <div className="flex items-start justify-between px-4 py-3 border-b shrink-0">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground font-mono">
            #{customer.customer_number ?? "—"}
          </p>
          <h2 className="text-lg font-semibold truncate">{customer.name}</h2>
          <Badge variant="outline" className="text-[10px] mt-1">
            {TYPE_LABELS[customer.customer_type || "other"] || customer.customer_type}
          </Badge>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 px-4 py-4">
        <div className="space-y-4 text-sm">
          {/* Contact info grid */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-muted-foreground text-xs">Contact</p>
              <p>{customer.contact_name || "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Email</p>
              <p className="break-all">{customer.email || "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Phone</p>
              <p>{customer.phone || "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Projects</p>
              <p>{projectCounts?.total ?? 0} total, {projectCounts?.active ?? 0} active</p>
            </div>
          </div>

          {/* Billing Address */}
          {(customer.billing_address || customer.billing_city) && (
            <div>
              <p className="text-muted-foreground text-xs mb-1">Billing Address</p>
              <p>{[customer.billing_address, customer.billing_city, customer.billing_state, customer.billing_zip].filter(Boolean).join(", ")}</p>
            </div>
          )}

          {/* Job Address */}
          {(customer.job_address || customer.job_city) && (
            <div>
              <p className="text-muted-foreground text-xs mb-1">Job Address</p>
              <p>{[customer.job_address, customer.job_city, customer.job_state, customer.job_zip].filter(Boolean).join(", ")}</p>
            </div>
          )}

          {/* Notes */}
          {customer.notes && (
            <div>
              <p className="text-muted-foreground text-xs mb-1">Notes</p>
              <p className="whitespace-pre-wrap">{customer.notes}</p>
            </div>
          )}

          {/* QuickBooks */}
          <div className="border-t pt-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">QuickBooks</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">ID: {customer.qb_customer_id || "—"}</span>
              {syncBadge(customer.qb_sync_status)}
            </div>
          </div>

          {/* Created date */}
          <div className="border-t pt-3">
            <p className="text-xs text-muted-foreground">
              Created: {format(new Date(customer.created_at), "MMM d, yyyy")}
            </p>
          </div>
        </div>
      </ScrollArea>

      {/* Footer actions */}
      <div className="flex items-center gap-2 px-4 py-3 border-t shrink-0">
        <Button variant="outline" size="sm" onClick={onViewFull}>
          <ExternalLink className="mr-1 h-3 w-3" />Full Details
        </Button>
        {canManage && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-1 h-3 w-3" />Edit
          </Button>
        )}
        {canManage && (
          <Button variant="destructive" size="sm" disabled={isDeleting} onClick={onDelete}>
            <Trash2 className="mr-1 h-3 w-3" />Delete
          </Button>
        )}
      </div>
    </div>
  );
};
